import { FC, useMemo, useState } from 'react';

import { CampaignSubSource, HH_SPLIT_SUB_SOURCES } from 'utils/constants/campaign';

import {
    ActionBar,
    BottomSheet,
    Button,
    CheckableChip,
    ChipsContainer,
    Modal,
    NavBar,
    Text,
    useBreakpoint,
    VSpacing,
} from '@hh.ru/magritte-ui';
import { CrossOutlinedSize24 } from '@hh.ru/magritte-ui/icon';

import useTrls from 'hooks/translation/useTrls';

import { MagritteModalHeader } from 'components/common/MagritteModalHeader/MagritteModalHeader';
import HH from 'components/CpcFlexibleCreate/SourcesAndBudgetsSidebar/AdExamplesCard/AdExamplesModal/images/hh.jpg';
import VKAdsNetwork from 'components/CpcFlexibleCreate/SourcesAndBudgetsSidebar/AdExamplesCard/AdExamplesModal/images/vk_ads_network.jpg';
import VKAdsOK from 'components/CpcFlexibleCreate/SourcesAndBudgetsSidebar/AdExamplesCard/AdExamplesModal/images/vk_ads_ok.jpg';
import VKAdsProject from 'components/CpcFlexibleCreate/SourcesAndBudgetsSidebar/AdExamplesCard/AdExamplesModal/images/vk_ads_project.jpg';
import VKAdsVK from 'components/CpcFlexibleCreate/SourcesAndBudgetsSidebar/AdExamplesCard/AdExamplesModal/images/vk_ads_vk.jpg';
import YandexSearch from 'components/CpcFlexibleCreate/SourcesAndBudgetsSidebar/AdExamplesCard/AdExamplesModal/images/yandex_direct.jpg';
import YandexNetwork from 'components/CpcFlexibleCreate/SourcesAndBudgetsSidebar/AdExamplesCard/AdExamplesModal/images/yandex_network.jpg';

import { useSelector } from 'store/utils';

import styles from 'components/CpcFlexibleCreate/SourcesAndBudgetsSidebar/AdExamplesCard/AdExamplesModal/AdExamplesModal.module.less';

interface AdExamplesModalProps {
    isModalVisible: boolean;
    hideModal: () => void;
}

const AD_EXAMPLE: Partial<Record<CampaignSubSource, { chipTrlKey: string; image: string }>> = {
    [CampaignSubSource.HH]: { chipTrlKey: 'AdCreate.AdForm.AdSources.AdExamplesModal.chips.HH', image: HH },
    [CampaignSubSource.VKAdsOK]: {
        chipTrlKey: 'AdCreate.AdForm.AdSources.AdExamplesModal.chips.VKAdsOK',
        image: VKAdsOK,
    },
    [CampaignSubSource.VKAdsVK]: {
        chipTrlKey: 'AdCreate.AdForm.AdSources.AdExamplesModal.chips.VKAdsVK',
        image: VKAdsVK,
    },
    [CampaignSubSource.VKAdsNetwork]: {
        chipTrlKey: 'AdCreate.AdForm.AdSources.AdExamplesModal.chips.VKAdsNetwork',
        image: VKAdsNetwork,
    },
    [CampaignSubSource.VKAdsProject]: {
        chipTrlKey: 'AdCreate.AdForm.AdSources.AdExamplesModal.chips.VKAdsProject',
        image: VKAdsProject,
    },
    [CampaignSubSource.YandexNetwork]: {
        chipTrlKey: 'AdCreate.AdForm.AdSources.AdExamplesModal.chips.YandexNetwork',
        image: YandexNetwork,
    },
    [CampaignSubSource.YandexSearch]: {
        chipTrlKey: 'AdCreate.AdForm.AdSources.AdExamplesModal.chips.YandexSearch',
        image: YandexSearch,
    },
};

const AD_EXAMPLES_SUB_SOURCES = [
    CampaignSubSource.HH,
    CampaignSubSource.YandexSearch,
    CampaignSubSource.YandexNetwork,
    CampaignSubSource.VKAdsVK,
    CampaignSubSource.VKAdsOK,
    CampaignSubSource.VKAdsProject,
    CampaignSubSource.VKAdsNetwork,
];

const TrlKeys = {
    title: 'AdCreate.AdForm.AdSources.AdExamplesModal.title',
    button: 'AdCreate.AdForm.AdSources.AdExamplesModal.close',
};

export const AdExamplesModal: FC<AdExamplesModalProps> = ({ isModalVisible, hideModal }) => {
    const adSourcesConfig = useSelector((state) => state.createCampaign.adSourcesConfig);

    const allSubSources = useMemo(
        () =>
            adSourcesConfig.reduce((acc, source) => {
                // exp_PORTFOLIO-53635_start
                const subSource = HH_SPLIT_SUB_SOURCES.includes(source.subSource)
                    ? CampaignSubSource.HH
                    : source.subSource;
                // exp_PORTFOLIO-53635_end
                if (AD_EXAMPLES_SUB_SOURCES.includes(subSource) && !acc.includes(subSource)) {
                    acc.push(subSource);
                }
                return acc;
            }, [] as CampaignSubSource[]),
        [adSourcesConfig]
    );

    const [activeChip, setActiveChip] = useState(CampaignSubSource.HH);

    const trls = useTrls();
    const { isMobile } = useBreakpoint();

    const actions = (
        <ActionBar
            primaryActions={
                <Button
                    stretched={isMobile}
                    mode="primary"
                    style="accent"
                    onClick={hideModal}
                    data-qa="hide-ad-examples-modal"
                >
                    {trls(TrlKeys.button)}
                </Button>
            }
        />
    );

    const chipsContent = allSubSources.map((subSource) => (
        <CheckableChip
            key={subSource}
            size="small"
            type="radio"
            name="subsources"
            value={subSource}
            checked={activeChip === subSource}
            onChange={({ target }) => setActiveChip(target.value as CampaignSubSource)}
        >
            {trls(AD_EXAMPLE[subSource]?.chipTrlKey)}
        </CheckableChip>
    ));

    const chipsContainer = isMobile ? (
        <ChipsContainer scrollable noWrap>
            {chipsContent}
        </ChipsContainer>
    ) : (
        <ChipsContainer>{chipsContent}</ChipsContainer>
    );

    const content = (
        <div className={styles.content}>
            {chipsContainer}
            <VSpacing default={16} />
            <div className={styles.imageContainer}>
                <img
                    className={styles.image}
                    src={AD_EXAMPLE[activeChip]?.image}
                    alt={trls(AD_EXAMPLE[activeChip]?.chipTrlKey)}
                />
            </div>
        </div>
    );

    return (
        <>
            <Modal
                visible={isModalVisible}
                onClose={hideModal}
                size="large"
                header={
                    <MagritteModalHeader
                        title={trls(TrlKeys.title)}
                        actions={<CrossOutlinedSize24 initialColor="secondary" onClick={hideModal} />}
                    />
                }
                footer={actions}
            >
                {content}
            </Modal>
            <BottomSheet
                withContentPaddings={false}
                header={
                    <NavBar>
                        <NavBar.Pane>
                            <NavBar.Stage>
                                <NavBar.Actions left centered right={<CrossOutlinedSize24 onClick={hideModal} />}>
                                    <NavBar.TitleContainer
                                        title={<Text typography="subtitle-1-semibold">{trls(TrlKeys.title)}</Text>}
                                    />
                                </NavBar.Actions>
                            </NavBar.Stage>
                        </NavBar.Pane>
                    </NavBar>
                }
                visible={isModalVisible}
                onClose={hideModal}
                footer={actions}
                height="full-screen"
            >
                <VSpacing default={16} />
                {content}
            </BottomSheet>
        </>
    );
};
